/* eslint-disable no-unused-vars */
import React, { useState, useRef, useEffect } from 'react';
import { Formik, Form } from 'formik';
import * as Yup from 'yup';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import useBuildRegisterForm, {
  BuildForm,
} from '../../../../hooks/buildRegisterForm/useBuildRegisterForm';
import RegisterNavigation from '../RegisterNavigation/RegisterNavigation';
import styles from './RegisterFormStep.module.scss';
import allActions from '../../../../store/actions';
import SummaryStep from '../../../../views/SummaryStep';
import StepsNavigation from '../../../StepsNavigation/StepsNavigation';
import buildPATHS from '../../../../utils/paths';
import { mapDataBeforeSave } from '../../../../utils/FormDataMappers';
import {
  formInitialValuesSelector,
  languageSelector,
  previewFormSelector,
} from '../../../../store/selectors/appSelectors';
import {
  acceptedStepsSelector,
  currentStepSelector,
  customFieldsSelector,
  disableSubmitSelector,
  isRedirectFromSummarySelector,
  registerDataSelector,
  stepsSelector,
} from '../../../../store/selectors/formConfigSelectors';
import {
  showSummarySelector,
  stepSubmitOnSelector,
} from '../../../../store/selectors/globalConfigSelectors';
import { PATHS_WITH_PARAMS, getPathWithBaseUrl } from '../../../../utils/paths';
import { NotificationError } from '../../../../utils/notification';

const RegisterFormStep = (props) => {
  const { toasts, setToasts } = props;

  const [formikValues, setFormikValues] = useState({});

  const {
    initialValues,
    currentConf,
    validationSchema,
    isFormBuilding,
    initialTouched,
    initialErrors,
  } = useBuildRegisterForm(formikValues);
  const [formValidation, setFormValidation] = useState(validationSchema);
  const [stepToRedirect, setStepToRedirect] = useState(0);

  const {
    formInitialValues,
    language,
    previewForm,
    currentStep,
    isRedirectFromSummary,
    acceptedSteps,
    disableSubmit,
    stepsLength,
    showSummary,
    stepSubmitOn,
    registerData,
    customFields,
  } = useSelector((state) => ({
    // app state
    formInitialValues: formInitialValuesSelector(state),
    language: languageSelector(state),
    previewForm: previewFormSelector(state),
    // formConfig state
    currentStep: currentStepSelector(state),
    isRedirectFromSummary: isRedirectFromSummarySelector(state),
    acceptedSteps: acceptedStepsSelector(state),
    disableSubmit: disableSubmitSelector(state),
    stepsLength: stepsSelector(state)?.length,
    // globalConfig
    showSummary: showSummarySelector(state),
    stepSubmitOn: stepSubmitOnSelector(state),
    // formConfig state
    registerData: registerDataSelector(state),
    customFields: customFieldsSelector(state),
  }));

  useEffect(() => {
    setFormValidation(validationSchema);
  }, [validationSchema]);
  const history = useHistory();
  const dispatch = useDispatch();

  const isNextStepSummary = stepToRedirect === stepsLength;

  const getAllDataBeforeSend = (currentStepValues) => {
    const earlierRegisterData = registerData.reduce(
      (result, current) => ({ ...result, ...current }),
      {},
    );
    const formData = {
      ...(formInitialValues || {}),
      ...earlierRegisterData,
      ...currentStepValues,
    };
    return mapDataBeforeSave(formData, customFields);
  };

  const sendDataIfNoSummary = (values) => {
    const redirectUrl = getPathWithBaseUrl(PATHS_WITH_PARAMS.application_sent);

    if (previewForm) {
      dispatch(allActions.formConfigActions.submitPreviewForm());
      history.push(redirectUrl);
      return;
    }

    dispatch(
      allActions.formConfigActions.submitForm(
        {
          fields: getAllDataBeforeSend(values),
          language,
        },
        history,
        redirectUrl,
      ),
    );
  };

  const handleSubmit = async (values, { setSubmitting }) => {
    dispatch(allActions.formConfigActions.setRegisterData(values, currentStep));

    if (stepSubmitOn) {
      // Await saveStepForm and handle failure
      const result = await dispatch(
        allActions.formConfigActions.saveStepForm(
          {
            fields: getAllDataBeforeSend(values),
            language,
          },
          currentStep,
        ),
      );
      if (result?.status === false) {
        setToasts([...toasts, ...NotificationError(result.message)]);
        return;
      }
    }

    dispatch(allActions.formConfigActions.setAcceptedSteps(currentStep, true));

    // if(currentStep === 2) {
    //   const shouldShowAlertMsg = showAlertMsg(values)
    //   if(shouldShowAlertMsg) {
    //     return
    //   }
    // }

    if (!previewForm) {
      dispatch(
        allActions.appConfigActions.setFormInitialValues({
          ...formInitialValues,
          ...values,
        }),
      );
    }

    if (isRedirectFromSummary) {
      dispatch(allActions.formConfigActions.setFormStep(stepsLength));
      return;
    }

    setSubmitting(false);

    if (!isNextStepSummary) {
      dispatch(allActions.formConfigActions.setFormStep(stepToRedirect));
      return;
    }

    const areAllStepsCorrect = acceptedSteps.every(
      (accStep) => accStep === true,
    );
    if (!areAllStepsCorrect) return;

    if (!showSummary) {
      sendDataIfNoSummary(values);
      return;
    }

    dispatch(allActions.formConfigActions.setBackFromSummary(true));
    dispatch(allActions.formConfigActions.setFormStep(stepsLength));
  };

  if (currentStep === stepsLength)
    return (
      <>
        <StepsNavigation />
        <SummaryStep />
      </>
    );

  const buildFormValidation = () => {
    return Yup.object().shape(formValidation);
  };
  const handleValidation = (validationObj) => {
    setFormValidation({ ...formValidation, ...validationObj });
  };

  return (
    <>
      <Formik
        enableReinitialize
        initialValues={initialValues}
        initialTouched={initialTouched}
        initialErrors={initialErrors}
        validationSchema={buildFormValidation()}
        onSubmit={handleSubmit}
      >
        {({ submitForm, values, validateForm }) => {
          // Keep formikValues in sync with actual Formik values using useEffect
          useEffect(() => {
            setFormikValues(values);
          }, [values]);

          return (
            <>
              <StepsNavigation
                formValues={values}
                validateForm={validateForm}
              />
              <Form className={styles.form}>
                {isFormBuilding ? null : (
                  <>
                    <BuildForm
                      currentFormConfig={currentConf}
                      handleValidation={handleValidation}
                    />
                    <RegisterNavigation
                      formValues={values}
                      setStepToRedirect={setStepToRedirect}
                      submitForm={submitForm}
                      disabled={disableSubmit}
                      validateForm={validateForm}
                    />
                  </>
                )}
              </Form>
            </>
          );
        }}
      </Formik>
    </>
  );
};

export default RegisterFormStep;
